/*
  Perioden-Kennzahlen für den Shiftplan V2.
*/

import { parseDateValue } from "../date/month";
import { getShiftDayKeys } from "./selectors";
import { getUserHolidayDayKeys } from "./userHolidays";
import { buildEmployeeWorkloadMap } from "./workload";

const DEFAULT_WORKING_DAYS_PER_WEEK = 5;

function parseNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function roundHours(value) {
  return Math.round(parseNumber(value) * 100) / 100;
}

function normalizeCount(value) {
  return Math.max(0, Math.round(parseNumber(value)));
}

function isWorkingDay(date, workingDaysPerWeek) {
  const weekday = date.getDay();
  const normalizedWorkingDays = Math.max(
    1,
    Math.min(7, Number(workingDaysPerWeek || DEFAULT_WORKING_DAYS_PER_WEEK))
  );

  if (normalizedWorkingDays >= 7) return true;
  if (normalizedWorkingDays === 6) return weekday !== 0;
  return weekday >= 1 && weekday <= normalizedWorkingDays;
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function isVacationAbsence(shift, vacationAbsenceIds) {
  if (shift?.entryType !== "absence") return false;
  if (vacationAbsenceIds.has(String(shift?.absenceId || ""))) return true;

  return normalizeText(shift?.title || shift?.department) === "ferien";
}

function buildVisibleDayMaps(days) {
  const visibleDaysByKey = new Map();
  const visibleDayKeys = new Set();

  (Array.isArray(days) ? days : []).forEach((day) => {
    const key = String(day?.key || "").trim();
    if (!key) return;
    visibleDayKeys.add(key);
    visibleDaysByKey.set(key, day);
  });

  return { visibleDaysByKey, visibleDayKeys };
}

function addVacationDayKeysFromLegacyAbsences({
  target,
  shifts,
  visibleDaysByKey,
  visibleDayKeys,
  workingDaysPerWeek,
  vacationAbsenceIds,
}) {
  (Array.isArray(shifts) ? shifts : []).forEach((shift) => {
    if (!isVacationAbsence(shift, vacationAbsenceIds)) return;

    const employeeId = String(shift?.employeeId || "");
    if (!employeeId) return;

    getShiftDayKeys(shift).forEach((dateKey) => {
      if (!visibleDayKeys.has(dateKey)) return;

      const visibleDay = visibleDaysByKey.get(dateKey);
      const visibleDate = parseDateValue(visibleDay?.date || dateKey);
      if (visibleDate && !isWorkingDay(visibleDate, workingDaysPerWeek)) return;

      target.add(`${employeeId}::${dateKey}`);
    });
  });
}

function countAbsenceDays({ shifts, visibleDayKeys, vacationAbsenceIds }) {
  const absenceDayKeys = new Set();

  (Array.isArray(shifts) ? shifts : []).forEach((shift) => {
    if (shift?.entryType !== "absence" || isVacationAbsence(shift, vacationAbsenceIds)) return;

    const employeeId = String(shift?.employeeId || "");
    if (!employeeId) return;

    getShiftDayKeys(shift).forEach((dateKey) => {
      if (visibleDayKeys.has(dateKey)) absenceDayKeys.add(`${employeeId}::${dateKey}`);
    });
  });

  return absenceDayKeys.size;
}

function countVacationDays({ employees, shifts, days, workingDaysPerWeek, vacationAbsenceIds, userHolidays }) {
  const { visibleDaysByKey, visibleDayKeys } = buildVisibleDayMaps(days);
  const employeeIds = new Set(
    (Array.isArray(employees) ? employees : []).map((employee) => String(employee?.id || ""))
  );
  const vacationDayKeys = new Set();

  addVacationDayKeysFromLegacyAbsences({
    target: vacationDayKeys,
    shifts,
    visibleDaysByKey,
    visibleDayKeys,
    workingDaysPerWeek,
    vacationAbsenceIds,
  });

  (Array.isArray(userHolidays) ? userHolidays : []).forEach((holiday) => {
    const employeeId = String(holiday?.userId || "");
    if (!employeeId || !employeeIds.has(employeeId)) return;

    getUserHolidayDayKeys(holiday).forEach((dateKey) => {
      if (!visibleDayKeys.has(dateKey)) return;
      const visibleDay = visibleDaysByKey.get(dateKey);
      const dayDate = parseDateValue(visibleDay?.date || dateKey);
      if (dayDate && !isWorkingDay(dayDate, workingDaysPerWeek)) return;
      vacationDayKeys.add(`${employeeId}::${dateKey}`);
    });
  });

  return vacationDayKeys.size;
}

export function normalizePeriodSummary(summary = {}) {
  return {
    plannedHours: roundHours(summary.planned_hours ?? summary.plannedHours),
    shiftCount: normalizeCount(summary.shift_count ?? summary.shiftCount),
    absenceCount: normalizeCount(summary.absence_count ?? summary.absenceCount),
    vacationCount: normalizeCount(summary.vacation_count ?? summary.vacationCount),
    source: String(summary.source || "frontend"),
  };
}

export function buildPeriodSummary({
  employees,
  shifts,
  days,
  workingDaysPerWeek,
  absenceOptions = [],
  vacationAbsenceIds = new Set(),
  userHolidays = [],
}) {
  const normalizedVacationAbsenceIds =
    vacationAbsenceIds instanceof Set ? vacationAbsenceIds : new Set(Array.from(vacationAbsenceIds || []));
  const { visibleDayKeys } = buildVisibleDayMaps(days);
  const periodShifts = (Array.isArray(shifts) ? shifts : []).filter((shift) =>
    getShiftDayKeys(shift).some((dateKey) => visibleDayKeys.has(dateKey))
  );
  const workloadByEmployeeId = buildEmployeeWorkloadMap({
    employees,
    shifts: periodShifts,
    days,
    workingDaysPerWeek,
    absenceOptions,
    vacationAbsenceIds: normalizedVacationAbsenceIds,
    userHolidays,
  });

  const plannedHours = Object.values(workloadByEmployeeId).reduce(
    (total, workload) => total + parseNumber(workload?.plannedHours),
    0
  );

  return normalizePeriodSummary({
    plannedHours,
    shiftCount: periodShifts.filter((shift) => shift?.entryType !== "absence").length,
    absenceCount: countAbsenceDays({
      shifts: periodShifts,
      visibleDayKeys,
      vacationAbsenceIds: normalizedVacationAbsenceIds,
    }),
    vacationCount: countVacationDays({
      employees,
      shifts: periodShifts,
      days,
      workingDaysPerWeek,
      vacationAbsenceIds: normalizedVacationAbsenceIds,
      userHolidays,
    }),
  });
}
